/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Avatar,
  Rating,
  TablePagination,
  Skeleton,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Block,
  CheckCircle,
  Cancel,
  Phone,
  Email,
  DirectionsCar,
  LocationOn,
  Schedule,
  Undo,
  Description,
} from '@mui/icons-material';
import { getDrivers } from '../services/graphql';
import { GET_DRIVER_WITH_KYC, GET_KYC_APPLICATION, UPDATE_DRIVER_KYC_STATUS, VERIFY_KYC_DOCUMENT } from '../graphql/queries';
import type { KYCApplication, KYCDocument } from '../types/kyc';

const client = generateClient();

const DOC_LABELS: Record<string, string> = {
  DRIVERS_LICENSE: 'License',
  VEHICLE_REGISTRATION: 'Registration',
  INSURANCE: 'Insurance',
  PROFILE_PHOTO: 'Photo',
};

function DriverManagement() {
  const [drivers, setDrivers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState<any | null>(null);
  const [kycApplication, setKycApplication] = useState<KYCApplication | null>(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    getDrivers().then(data => {
      setDrivers(data);
      setLoading(false);
    });
  }, []);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getKycStatusColor = (status?: string) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'PENDING': return 'warning';
      default: return 'default';
    }
  };

  const handleStatusChange = (driverId: string, newStatus: string) => {
    setDrivers(prev => prev.map(d => d.id === driverId ? { ...d, status: newStatus } : d));
  };

  const filteredDrivers = drivers.filter(d =>
    statusFilter === 'all' || d.status?.toLowerCase() === statusFilter
  );

  const paginatedDrivers = filteredDrivers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleViewDriver = async (driver: any) => {
    setSelectedDriver(driver);
    setKycApplication(null);
    setKycError(null);
    setDialogOpen(true);

    setKycLoading(true);
    try {
      // Fetch driver and KYC independently so one failure doesn't block the other
      const [driverResult, kycResult] = await Promise.allSettled([
        client.graphql({
          query: GET_DRIVER_WITH_KYC,
          variables: { id: driver.id },
        }),
        client.graphql({
          query: GET_KYC_APPLICATION,
          variables: { driverId: driver.id },
        }),
      ]);

      // Handle driver result
      if (driverResult.status === 'fulfilled') {
        const driverResponse = driverResult.value as any;
        setSelectedDriver(driverResponse.data.getAdminDriver ?? driver);
      }

      // Handle KYC result — check for GraphQL-level errors (e.g. PermissionError)
      if (kycResult.status === 'fulfilled') {
        const kycResponse = kycResult.value as any;
        const gqlErrors = kycResponse.errors;
        if (gqlErrors?.length) {
          const permissionError = gqlErrors.find(
            (e: any) => e.errorType === 'PermissionError' || e.message?.includes('Access denied')
          );
          if (permissionError) {
            setKycError('You do not have permission to view KYC applications. Required role: Operations or SuperAdmin.');
          } else {
            setKycError(gqlErrors[0].message || 'Failed to load KYC application');
          }
          setKycApplication(null);
        } else {
          setKycApplication(kycResponse.data.getKycApplication ?? null);
        }
      } else {
        // Network-level or thrown error
        const err = kycResult.reason;
        const gqlErrors = err?.errors;
        if (gqlErrors?.length) {
          const permissionError = gqlErrors.find(
            (e: any) => e.errorType === 'PermissionError' || e.message?.includes('Access denied')
          );
          if (permissionError) {
            setKycError('You do not have permission to view KYC applications. Required role: Operations or SuperAdmin.');
          } else {
            setKycError(gqlErrors[0].message || 'Failed to load KYC application');
          }
        } else {
          setKycError(err?.message || 'Failed to load KYC application');
        }
        setKycApplication(null);
      }

      // If driver fetch itself failed, show that error too
      if (driverResult.status === 'rejected') {
        const driverErr = driverResult.reason;
        setKycError((prev: string | null) =>
          prev
            ? `${prev}\nFailed to load driver details: ${driverErr?.message || 'Unknown error'}`
            : `Failed to load driver details: ${driverErr?.message || 'Unknown error'}`
        );
      }
    } catch (err: any) {
      setKycError(err.message || 'Failed to load driver details');
    } finally {
      setKycLoading(false);
    }
  };

  const handleRevertApproval = async () => {
    if (!kycApplication) return;
    setProcessing(true);
    setKycError(null);
    try {
      await client.graphql({
        query: UPDATE_DRIVER_KYC_STATUS,
        variables: {
          input: {
            driverId: kycApplication.driverId,
            kycStatus: 'PENDING',
            reviewerName: 'Admin User',
          },
        },
      });
      setKycApplication(prev => prev ? { ...prev, status: 'PENDING' } : prev);
    } catch (err: any) {
      setKycError(err.message || 'Failed to revert approval');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnverifyDocument = async (doc: KYCDocument) => {
    if (!kycApplication) return;
    setProcessing(true);
    setKycError(null);
    try {
      const result: any = await client.graphql({
        query: VERIFY_KYC_DOCUMENT,
        variables: {
          input: {
            driverId: kycApplication.driverId,
            documentType: doc.type,
            verified: false,
            reviewerName: 'Admin User',
          },
        },
      });
      if (result.data.verifyKYCDocument.success) {
        // Re-fetch the full application so documents + approval flags are fresh
        const kycResult: any = await client.graphql({
          query: GET_KYC_APPLICATION,
          variables: { driverId: kycApplication.driverId },
        });
        setKycApplication(kycResult.data.getKycApplication ?? kycApplication);
      }
    } catch (err: any) {
      setKycError(err.message || 'Failed to unverify document');
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDriver(null);
    setKycApplication(null);
    setKycError(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Driver Management</Typography>
        <TextField
          select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Drivers</MenuItem>
          <MenuItem value="online">Online</MenuItem>
          <MenuItem value="offline">Offline</MenuItem>
          <MenuItem value="suspended">Suspended</MenuItem>
        </TextField>
      </Box>

      <Card elevation={0}>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Driver</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Performance</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(8)].map((__, j) => (
                        <TableCell key={j}><Skeleton width={100} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  paginatedDrivers.map((driver) => (
                    <TableRow key={driver.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar>{driver.name?.charAt(0) ?? '?'}</Avatar>
                          <Box>
                            <Typography variant="subtitle2">{driver.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Driver ID: {driver.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Phone fontSize="small" />
                          <Typography variant="body2">{driver.phone || 'N/A'}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Email fontSize="small" />
                          <Typography variant="body2">{driver.email || 'N/A'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DirectionsCar fontSize="small" />
                          <Typography variant="body2">Vehicle N/A</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={driver.status?.toUpperCase() ?? 'UNKNOWN'}
                          color={getStatusColor(driver.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Rating value={driver.rating ?? 0} readOnly size="small" />
                          <Typography variant="body2">({driver.rating ?? 0})</Typography>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {driver.totalTrips ?? 0} trips
                        </Typography>
                        {driver.riskProfile && (() => {
                          const rp = typeof driver.riskProfile === 'string' ? JSON.parse(driver.riskProfile) : driver.riskProfile;
                          const tierColor: Record<string, 'success' | 'info' | 'warning' | 'error'> = { TRUSTED: 'success', STANDARD: 'info', PROBATION: 'warning', SUSPENDED: 'error' };
                          return <Chip label={`${rp.tier ?? '?'} ${rp.score ?? '?'}`} color={tierColor[rp.tier] ?? 'default'} size="small" sx={{ mt: 0.5 }} />;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Schedule fontSize="small" color="action" />
                          <Typography variant="body2">Online</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="caption" color="textSecondary">
                            Last seen: 2 min ago
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label="V" color="success" size="small" title="Verified" />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDriver(driver)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {driver.status === 'active' && (
                            <Tooltip title="Suspend">
                              <IconButton size="small" color="warning" onClick={() => handleStatusChange(driver.id, 'suspended')}>
                                <Block />
                              </IconButton>
                            </Tooltip>
                          )}
                          {driver.status === 'suspended' && (
                            <Tooltip title="Reactivate">
                              <IconButton size="small" color="success" onClick={() => handleStatusChange(driver.id, 'active')}>
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredDrivers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </CardContent>
      </Card>

      {/* Driver Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Driver Details</DialogTitle>
        <DialogContent>
          {kycError && (
            <Alert
              severity={kycError.includes('permission') ? 'warning' : 'error'}
              sx={{ mb: 2 }}
            >
              {kycError}
            </Alert>
          )}
          {selectedDriver && (
            <Grid container spacing={3}>
              {/* Personal info */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Personal Information</Typography>
                {[
                  ['Name', selectedDriver.name],
                  ['Email', selectedDriver.email],
                  ['Phone', selectedDriver.phone || 'N/A'],
                ].map(([label, value]) => (
                  <Box mb={2} key={label}>
                    <Typography variant="subtitle2">{label}</Typography>
                    <Typography>{value}</Typography>
                  </Box>
                ))}
                <Box mb={2}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip
                    label={selectedDriver.status?.toUpperCase()}
                    color={getStatusColor(selectedDriver.status) as any}
                  />
                </Box>
              </Grid>

              {/* Performance */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Performance</Typography>
                <Box mb={2}>
                  <Typography variant="subtitle2">Rating</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Rating value={selectedDriver.rating ?? 0} readOnly />
                    <Typography>({selectedDriver.rating ?? 0})</Typography>
                  </Box>
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2">Total Trips</Typography>
                  <Typography>{selectedDriver.totalTrips ?? 0}</Typography>
                </Box>

                {/* Risk Profile */}
                {selectedDriver.riskProfile && (() => {
                  const rp = typeof selectedDriver.riskProfile === 'string'
                    ? JSON.parse(selectedDriver.riskProfile)
                    : selectedDriver.riskProfile;
                  const tierColor: Record<string, 'success' | 'info' | 'warning' | 'error'> = {
                    TRUSTED: 'success', STANDARD: 'info', PROBATION: 'warning', SUSPENDED: 'error',
                  };
                  return (
                    <Box mt={2} p={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle2">Risk Profile</Typography>
                        <Chip
                          label={rp.tier ?? 'UNKNOWN'}
                          color={tierColor[rp.tier] ?? 'default'}
                          size="small"
                        />
                        <Typography variant="body2" color="textSecondary">
                          Score: {rp.score ?? '—'}/100
                        </Typography>
                      </Box>
                      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                        <Typography variant="caption">Acceptance rate: {rp.acceptanceRate != null ? `${(rp.acceptanceRate * 100).toFixed(0)}%` : '—'}</Typography>
                        <Typography variant="caption">Completion rate: {rp.completionRate != null ? `${(rp.completionRate * 100).toFixed(0)}%` : '—'}</Typography>
                        <Typography variant="caption">Cancellation rate: {rp.cancellationRate != null ? `${(rp.cancellationRate * 100).toFixed(0)}%` : '—'}</Typography>
                        <Typography variant="caption">Forced offline: {rp.forcedOfflineCount ?? 0}×</Typography>
                        <Typography variant="caption">Trips (30d): {rp.tripsLast30Days ?? 0}</Typography>
                        <Typography variant="caption">Rating trend: {rp.ratingTrend ?? '—'}</Typography>
                      </Box>
                      {rp.flags?.length > 0 && (
                        <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                          {rp.flags.map((f: string) => (
                            <Chip key={f} label={f.replace(/_/g, ' ')} color="warning" size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                      {rp.lastCalculatedAt && (
                        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                          Last calculated: {new Date(rp.lastCalculatedAt).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </Grid>

              {/* KYC / Documents section */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h6">KYC Application</Typography>
                  {kycLoading && <CircularProgress size={20} />}
                </Box>

                {!kycLoading && !kycApplication && !kycError && (
                  <Typography color="textSecondary">No KYC application found.</Typography>
                )}

                {kycApplication && (
                  <Box>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Chip
                        label={kycApplication.status}
                        color={getKycStatusColor(kycApplication.status) as any}
                      />
                      {kycApplication.status === 'APPROVED' && (
                        <Tooltip title="Revert approval back to PENDING">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={processing ? <CircularProgress size={14} /> : <Undo />}
                            onClick={handleRevertApproval}
                            disabled={processing}
                          >
                            Revert Approval
                          </Button>
                        </Tooltip>
                      )}
                    </Box>

                    {kycApplication.vehicleInfo && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>Vehicle</Typography>
                        <Typography variant="body2">
                          {kycApplication.vehicleInfo.make} {kycApplication.vehicleInfo.model} ({kycApplication.vehicleInfo.year}) — {kycApplication.vehicleInfo.color}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Plate: {kycApplication.vehicleInfo.licensePlate}
                        </Typography>
                        {kycApplication.licenseNumber && (
                          <Typography variant="body2" color="textSecondary">
                            License #: {kycApplication.licenseNumber}
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Typography variant="subtitle2" gutterBottom>Documents</Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {kycApplication.documents.map((doc, idx) => (
                        <Box key={idx} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={DOC_LABELS[doc.type] ?? doc.type}
                            size="small"
                            icon={<Description />}
                            color={doc.verified ? 'success' : 'default'}
                            onClick={() => doc.viewUrl && window.open(doc.viewUrl, '_blank')}
                            sx={{ cursor: doc.viewUrl ? 'pointer' : 'default', minWidth: 120 }}
                          />
                          {doc.verified ? (
                            <Tooltip title="Unverify this document">
                              <Chip
                                label="Verified"
                                color="success"
                                size="small"
                                icon={<CheckCircle />}
                                onDelete={() => handleUnverifyDocument(doc)}
                                deleteIcon={processing ? <CircularProgress size={14} /> : <Cancel />}
                                disabled={processing}
                              />
                            </Tooltip>
                          ) : (
                            <Chip label="Unverified" size="small" variant="outlined" />
                          )}
                          {doc.verifiedAt && (
                            <Typography variant="caption" color="textSecondary">
                              by {doc.verifiedBy} on {new Date(doc.verifiedAt).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>

                    {kycApplication.rejectionReason && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        Rejection reason: {kycApplication.rejectionReason}
                      </Alert>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DriverManagement;
